import { useMemo, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import type { UserDisplayInfo } from "@/hooks";

interface OwnerSearchPopoverProps {
  userMap: Map<string, UserDisplayInfo> | null;
  onSelect: (userId: string) => void;
}

/**
 * The people list a popover shows when an owner field is being reassigned. Only active
 * memberships are offered — a former member is still in `userMap` so their name renders on the
 * entities they already own, but they cannot be handed a new one.
 *
 * The search runs here rather than in the `Command` primitive (`shouldFilter={false}`) because
 * a person searching for a colleague types a name prefix or an address, not a fuzzy score: a
 * plain case-insensitive substring over the name and the email is what they expect back.
 */
export function OwnerSearchPopover({ userMap, onSelect }: OwnerSearchPopoverProps) {
  const [query, setQuery] = useState("");

  const activeMembers = useMemo(
    () =>
      userMap
        ? Array.from(userMap.entries()).filter(
            ([, user]) => (user.membershipStatus ?? "active") === "active"
          )
        : [],
    [userMap]
  );

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = normalizedQuery
    ? activeMembers.filter(
        ([, user]) =>
          user.fullName.toLowerCase().includes(normalizedQuery) ||
          user.email.toLowerCase().includes(normalizedQuery)
      )
    : activeMembers;

  return (
    <Command shouldFilter={false} loop className="rounded-none bg-transparent p-0">
      <CommandInput
        autoFocus
        value={query}
        onValueChange={setQuery}
        placeholder="Search people..."
      />
      <CommandList className="max-h-60 overscroll-contain p-1">
        <CommandEmpty className="px-2 py-2 text-left text-muted-foreground">
          No results
        </CommandEmpty>
        {filtered.map(([userId, user]) => (
          <CommandItem
            key={userId}
            value={userId}
            data-testid="owner-search-option"
            data-user-id={userId}
            data-membership-status={user.membershipStatus ?? "active"}
            onSelect={() => onSelect(userId)}
          >
            <span className="truncate">{user.fullName}</span>
            <CommandShortcut className="min-w-0 truncate text-[11px] tracking-normal">
              {user.email}
            </CommandShortcut>
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}
